import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './AccountMenu.css';
import { signOut } from '../firebase/auth';

// First / last name from the Firebase user, falling back to the email local part.
export function nameParts(user) {
  const dn = (user.displayName || '').trim();
  if (dn) {
    const [first, ...rest] = dn.split(/\s+/);
    return { first, last: rest.join(' ') };
  }
  return { first: (user.email || 'Account').split('@')[0], last: '' };
}

export function initials(user) {
  const { first, last } = nameParts(user);
  return (last ? first.charAt(0) + last.charAt(0) : first.slice(0, 2)).toUpperCase();
}

// Round avatar: the Firebase photoURL when present, else initials. Falls back to
// initials if the image fails to load (Google photo URLs can 403).
export const Avatar = ({ user, size }) => {
  const [failed, setFailed] = useState(false);
  const cls = `avatar avatar-${size}`;
  if (user.photoURL && !failed) {
    return (
      <img
        className={cls}
        src={user.photoURL}
        alt=""
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span className={`${cls} avatar-fallback`} aria-hidden="true">
      {initials(user)}
    </span>
  );
};

// Signed-in account control shared by the main site header and the admin console:
// an avatar that reveals a card (name + menu links + sign out) on hover or click.
// `items` are the links shown above Sign out — each is `{ label, to }` for a
// route Link or `{ label, onClick }` for an action button. Defaults to Settings.
export const AccountMenu = ({ user, items = [{ label: 'Settings', to: '/settings' }] }) => {
  // `open` reflects hover previews; `pinned` (set by clicking the avatar) keeps
  // the card open until you click away, click the avatar again, or press Escape.
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const ref = useRef(null);
  const { first, last } = nameParts(user);

  const close = () => {
    setOpen(false);
    setPinned(false);
  };

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const togglePinned = () => {
    if (pinned) {
      setPinned(false);
      setOpen(false);
    } else {
      setPinned(true);
      setOpen(true);
    }
  };

  return (
    <div
      className="avatar-menu"
      ref={ref}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => {
        if (!pinned) setOpen(false);
      }}
    >
      <button
        type="button"
        className="avatar-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={togglePinned}
      >
        <Avatar user={user} size="sm" />
      </button>
      {open && (
        <div className="avatar-card glass" role="menu">
          <div className="avatar-card-head">
            <Avatar user={user} size="lg" />
            <div className="avatar-name">
              <span className="avatar-first">{first}</span>
              {last && <span className="avatar-last">{last}</span>}
            </div>
          </div>
          {items.map((item) =>
            item.to ? (
              <Link
                key={item.label}
                to={item.to}
                className="avatar-settings"
                role="menuitem"
                onClick={close}
              >
                {item.label}
              </Link>
            ) : (
              <button
                key={item.label}
                type="button"
                className="avatar-settings"
                role="menuitem"
                onClick={() => {
                  close();
                  item.onClick();
                }}
              >
                {item.label}
              </button>
            )
          )}
          <button
            type="button"
            className="avatar-signout"
            role="menuitem"
            onClick={() => signOut()}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
